import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Flag } from "lucide-react";

/** Phase 10.7 — "Report an Error" public corrections entry point.
 * Drop-in: <ReportErrorButton context="candidate page — Jane Doe" defaultCategory="incorrect_candidate_info" /> */

const CATEGORIES = [
  { value: "incorrect_candidate_info", label: "Incorrect Candidate Information" },
  { value: "incorrect_ward_boundary", label: "Incorrect Ward Boundary" },
  { value: "outdated_source", label: "Outdated Source" },
  { value: "broken_link", label: "Broken Link" },
  { value: "other", label: "Other" },
];

export const ReportErrorButton = ({ context = "", defaultCategory = "", size = "sm", variant = "outline", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(defaultCategory);
  const [description, setDescription] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — hidden from humans
  const [sending, setSending] = useState(false);
  const [tracking, setTracking] = useState(null); // { reference, statusUrl } after success

  const submit = async (e) => {
    e.preventDefault();
    if (!category) { toast.error("Please choose what kind of error this is."); return; }
    if (description.trim().length < 10) { toast.error("Please describe the error (at least 10 characters)."); return; }
    setSending(true);
    try {
      const r = await api.post("/public/corrections", {
        category,
        description: (context ? `[${context}] ` : "") + description.trim(),
        page_url: window.location.pathname + window.location.search,
        suggested_correction: suggestion.trim() || null,
        source_url: sourceUrl.trim() || null,
        reporter_email: email.trim() || null,
        website: website || null,
      });
      toast.success("Report received. Thank you.");
      setTracking({ reference: r.data.tracking_reference, statusUrl: r.data.status_url });
      setDescription(""); setSuggestion(""); setSourceUrl(""); setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your report. Please try again.");
    } finally { setSending(false); }
  };

  const copyRef = () => {
    navigator.clipboard.writeText(tracking.reference)
      .then(() => toast.success("Tracking reference copied."))
      .catch(() => toast.error("Copy failed — please note it down manually."));
  };

  const close = (v) => { setOpen(v); if (!v) setTracking(null); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <Button size={size} variant={variant} className={className} data-testid="report-error-btn">
          <Flag className="h-3.5 w-3.5 mr-1.5" /> Report an error
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        {tracking ? (
          <>
            <DialogHeader>
              <DialogTitle>Report received</DialogTitle>
              <DialogDescription>
                Save this tracking reference — it is the only way to follow your report, and it is
                private to you.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-slate-200 bg-secondary p-4" data-testid="report-error-tracking">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Tracking reference</p>
              <p className="font-mono text-sm text-foreground break-all" data-testid="report-error-reference">{tracking.reference}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copyRef} data-testid="report-error-copy-ref">
                Copy reference
              </Button>
              <Button size="sm" className="bg-primary hover:bg-primary/90" asChild data-testid="report-error-view-status">
                <a href={tracking.statusUrl}>View status page</a>
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Status path: Received → Under Review → Confirmed → Corrected (or Dismissed with an
              explanation). Your identity is never shown on the status page.
            </p>
          </>
        ) : (
        <>
        <DialogHeader>
          <DialogTitle>Report an error</DialogTitle>
          <DialogDescription>
            Spotted something wrong — candidate details, a ward boundary, an outdated source or a broken
            link? Tell us. Every report is reviewed, and confirmed corrections are applied with an audit trail.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">What kind of error?</Label>
            <Select value={category || undefined} onValueChange={setCategory}>
              <SelectTrigger className="mt-1 bg-white" data-testid="report-error-category">
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Describe the error</Label>
            <Textarea className="mt-1" required minLength={10} maxLength={2000} value={description}
                      placeholder="What is wrong, and where did you see it?"
                      onChange={(e) => setDescription(e.target.value)} data-testid="report-error-description" />
          </div>
          <div>
            <Label className="text-xs">Suggested correction (optional)</Label>
            <Textarea className="mt-1" maxLength={2000} value={suggestion}
                      onChange={(e) => setSuggestion(e.target.value)} data-testid="report-error-suggestion" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Supporting source URL (optional)</Label>
              <Input className="mt-1" maxLength={800} placeholder="https://…" value={sourceUrl}
                     onChange={(e) => setSourceUrl(e.target.value)} data-testid="report-error-source" />
            </div>
            <div>
              <Label className="text-xs">Your email (optional, for follow-up only)</Label>
              <Input className="mt-1" type="email" maxLength={200} value={email}
                     onChange={(e) => setEmail(e.target.value)} data-testid="report-error-email" />
            </div>
          </div>
          {/* honeypot — hidden from humans, bots fill it */}
          <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)}
                 autoComplete="off" tabIndex={-1} aria-hidden="true"
                 style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
          <Button type="submit" disabled={sending} className="bg-primary hover:bg-primary/90 w-full"
                  data-testid="report-error-submit">
            {sending ? "Sending…" : "Send report"}
          </Button>
          <p className="text-[10px] text-muted-foreground">
            Your email is never published and is used only if we need to follow up on this report.
          </p>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
